import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Building2, KeyRound, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Workspace() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("create");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [co, setCo] = useState({ name: "", industry: "", size: "1-10", description: "", title: "Founder & CEO" });
  const [join, setJoin] = useState({ invite_code: "", title: "", department: "" });

  const run = async (fn) => {
    setError("");
    setBusy(true);
    try {
      await fn();
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-[520px] bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Welcome, {user.name?.split(" ")[0]}</h1>
            <p className="text-sm text-muted-foreground mt-1">Create your company's workspace, or join one with an invite code.</p>
          </div>
          <Button variant="ghost" size="icon" aria-label="Log out" onClick={async () => { await logout(); navigate("/login"); }} data-testid="workspace-logout-button"><LogOut className="h-4 w-4" /></Button>
        </div>
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }} className="mt-6">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="create" data-testid="workspace-create-tab"><Building2 className="h-4 w-4 mr-1.5" />Create company</TabsTrigger>
            <TabsTrigger value="join" data-testid="workspace-join-tab"><KeyRound className="h-4 w-4 mr-1.5" />Join with code</TabsTrigger>
          </TabsList>
          <TabsContent value="create">
            <form className="space-y-4 mt-5" onSubmit={(e) => { e.preventDefault(); run(() => api.post("/companies", co)); }}>
              <div className="space-y-1.5">
                <Label>Company name</Label>
                <Input required value={co.name} onChange={(e) => setCo({ ...co, name: e.target.value })} placeholder="Acme Ltd" data-testid="workspace-company-name-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Industry</Label>
                  <Input value={co.industry} onChange={(e) => setCo({ ...co, industry: e.target.value })} placeholder="e.g. Retail" data-testid="workspace-industry-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Team size</Label>
                  <Select value={co.size} onValueChange={(v) => setCo({ ...co, size: v })}>
                    <SelectTrigger data-testid="workspace-size-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["1-10", "11-50", "51-200", "201-500", "500+"].map((s) => <SelectItem key={s} value={s}>{s} people</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Your job title</Label>
                <Input value={co.title} onChange={(e) => setCo({ ...co, title: e.target.value })} data-testid="workspace-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label>What does the company do? <span className="text-muted-foreground font-normal">(helps the AI CEO)</span></Label>
                <Textarea value={co.description} onChange={(e) => setCo({ ...co, description: e.target.value })} rows={3} data-testid="workspace-description-input" />
              </div>
              {error && tab === "create" && <p className="text-sm text-destructive" data-testid="workspace-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={busy} data-testid="workspace-create-submit-button">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create workspace
              </Button>
            </form>
          </TabsContent>
          <TabsContent value="join">
            <form className="space-y-4 mt-5" onSubmit={(e) => { e.preventDefault(); run(() => api.post("/companies/join", join)); }}>
              <div className="space-y-1.5">
                <Label>Invite code</Label>
                <Input required value={join.invite_code} onChange={(e) => setJoin({ ...join, invite_code: e.target.value.toUpperCase() })} placeholder="8-character code" className="font-mono tracking-widest uppercase" data-testid="workspace-invite-code-input" />
                <p className="text-xs text-muted-foreground">Ask your company owner. It's in Settings & team.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Your job title</Label>
                  <Input value={join.title} onChange={(e) => setJoin({ ...join, title: e.target.value })} data-testid="workspace-join-title-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Department</Label>
                  <Input value={join.department} onChange={(e) => setJoin({ ...join, department: e.target.value })} data-testid="workspace-join-department-input" />
                </div>
              </div>
              {error && tab === "join" && <p className="text-sm text-destructive" data-testid="workspace-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={busy} data-testid="workspace-join-submit-button">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Join workspace
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
